import type { ReactNode } from "react";
import { CheckCircle2, CircleAlert, Clock } from "lucide-react";
import {
  getGoogleConnectionPresentation,
  getGoogleConnectionPresentationStyle,
  type GoogleConnectionTone,
  type GoogleIntegrationStatus,
} from "@/lib/googleIntegrationStatus";

type ConnectionStatusBadgeProps = {
  tone: GoogleConnectionTone;
  label: string;
  icon?: ReactNode;
};

function ConnectionStatusIcon({
  tone,
  className = "h-4 w-4 shrink-0",
}: {
  tone: GoogleConnectionTone;
  className?: string;
}) {
  const { iconClassName } = getGoogleConnectionPresentationStyle(tone);
  const iconClass = `${className} ${iconClassName}`;

  if (tone === "connected") {
    return <CheckCircle2 className={iconClass} strokeWidth={2} aria-hidden />;
  }

  if (tone === "pending-link") {
    return <Clock className={iconClass} strokeWidth={2} aria-hidden />;
  }

  return <CircleAlert className={iconClass} strokeWidth={2} aria-hidden />;
}

export function ConnectionStatusBadge({
  tone,
  label,
  icon,
}: ConnectionStatusBadgeProps) {
  const { badgeClassName } = getGoogleConnectionPresentationStyle(tone);

  return (
    <div
      className={`inline-flex items-center justify-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${badgeClassName}`}
    >
      {icon ?? <ConnectionStatusIcon tone={tone} />}
      <span className="pt-0.5 text-nowrap">{label}</span>
    </div>
  );
}

export function GoogleConnectionStatusBadge({
  status,
  workspaceId,
}: {
  status: GoogleIntegrationStatus | null;
  workspaceId?: string | null;
}) {
  const { label, tone } = getGoogleConnectionPresentation(status, workspaceId);
  return <ConnectionStatusBadge tone={tone} label={label} />;
}

export function LineBindStatusBadge({ bound }: { bound: boolean }) {
  return (
    <ConnectionStatusBadge
      tone={bound ? "connected" : "disconnected"}
      label={bound ? "ผูกบัญชีแล้ว" : "ยังไม่ได้ผูกบัญชี"}
    />
  );
}
